import NotesCell from './NotesCell.svelte';
import type { AgPromise, ICellRendererComp, ICellRendererParams } from '@ag-grid-community/core';
import { signal } from '@preact/signals-react';
import type { SymbolNotesService } from 'src/services/SymbolNotesService';
import { mount, unmount } from 'svelte';

export interface NotesCellRendererParams {
    symbolFieldId: string;
    notesService: SymbolNotesService;
}

// Track if service initialization has been started
let initializationStarted = false;

/**
 * Creates a cell renderer class for the Notes column.
 * The renderer displays an inline editable note for each symbol.
 */
export const makeNotesCellRenderer = ({
    symbolFieldId,
    notesService,
}: NotesCellRendererParams): { new (): ICellRendererComp } => {
    return class CellRenderer implements ICellRendererComp {
        div: HTMLDivElement = document.createElement('div');
        root: ReturnType<typeof mount> | undefined;

        symbol = signal<string | undefined>(undefined);

        getGui(): HTMLElement {
            this.div.className = 'w-full h-full';
            return this.div;
        }

        #mapParamsToSignals(params: ICellRendererParams<any, any>): void {
            if (!params.data || !symbolFieldId) {
                this.symbol.value = undefined;
                return;
            }

            this.symbol.value = params.data[symbolFieldId] ?? undefined;
        }

        init(params: ICellRendererParams<any, any>): void | AgPromise<void> {
            // Lazily initialize the notes service on first cell render
            if (!initializationStarted) {
                initializationStarted = true;
                notesService.initialize();
            }

            this.#mapParamsToSignals(params);

            this.root = mount(NotesCell, {
                target: this.div,
                props: {
                    symbol: this.symbol,
                    notesService,
                },
            });
        }

        refresh(params: ICellRendererParams<any, any>): boolean {
            this.#mapParamsToSignals(params);
            return true;
        }

        destroy(): void {
            if (this.root) {
                unmount(this.root);
            }
        }
    };
};
